import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { COGNITO_USER_POOL_ID, COGNITO_CLIENT_ID } from '../config';

const poolData = {
    UserPoolId: COGNITO_USER_POOL_ID,
    ClientId: COGNITO_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

export const authService = {
    // 1. Sign Up
    signUp: (username, password, email) => {
        return new Promise((resolve, reject) => {
            const attributeList = [];

            // If email is required, add it to attributes
            if (email) {
                attributeList.push({
                    Name: 'email',
                    Value: email,
                });
            }

            userPool.signUp(username, password, attributeList, null, (err, result) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result.user);
            });
        });
    },

    // 2. Confirm Sign Up (Code Verification)
    confirmSignUp: (username, code) => {
        return new Promise((resolve, reject) => {
            const userData = {
                Username: username,
                Pool: userPool
            };
            const cognitoUser = new CognitoUser(userData);

            cognitoUser.confirmRegistration(code, true, (err, result) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(result);
            });
        });
    },

    // 3. Login
    login: (username, password) => {
        return new Promise((resolve, reject) => {
            const authenticationData = {
                Username: username,
                Password: password
            };
            const authenticationDetails = new AuthenticationDetails(authenticationData);

            const userData = {
                Username: username,
                Pool: userPool
            };
            const cognitoUser = new CognitoUser(userData);

            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (result) => {
                    resolve(result);
                },
                onFailure: (err) => {
                    reject(err);
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    // Handle forced password change if needed (uncommon for public apps)
                    // Currently just deleting these to allow simple login
                    delete userAttributes.email_verified;
                    delete userAttributes.phone_number_verified;
                    resolve({ newPasswordRequired: true, userAttributes });
                }
            });
        });
    },

    // 4. Get Current Configured User (Context)
    getCurrentUser: () => {
        return userPool.getCurrentUser();
    },

    // 5. Get Session (Token)
    // This automatically refreshes the token if needed
    getSession: () => {
        return new Promise((resolve, reject) => {
            const cognitoUser = userPool.getCurrentUser();
            if (!cognitoUser) {
                reject("No user logged in");
                return;
            }

            cognitoUser.getSession((err, session) => {
                if (err) {
                    reject(err);
                    return;
                }
                resolve(session);
            });
        });
    },

    // 6. Sign Out
    logout: () => {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
            cognitoUser.signOut();
        }
        localStorage.removeItem('authToken'); // Clear legacy/cached tokens if any
    },

    // 7. Forgot Password (Initiate)
    forgotPassword: (username) => {
        return new Promise((resolve, reject) => {
            const userData = {
                Username: username,
                Pool: userPool
            };
            const cognitoUser = new CognitoUser(userData);

            cognitoUser.forgotPassword({
                onSuccess: (result) => {
                    resolve(result);
                },
                onFailure: (err) => {
                    reject(err);
                }
                // Optional: inputVerificationCode() is handled by the generic success/failure in many flows,
                // but usually forgotPassword requires the user to explicitly submit the code next.
            });
        });
    },

    // 8. Confirm Password (Reset)
    confirmPassword: (username, code, newPassword) => {
        return new Promise((resolve, reject) => {
            const userData = {
                Username: username,
                Pool: userPool
            };
            const cognitoUser = new CognitoUser(userData);

            cognitoUser.confirmPassword(code, newPassword, {
                onSuccess: () => {
                    resolve();
                },
                onFailure: (err) => {
                    reject(err);
                }
            });
        });
    }
};
